import LoginHintStore from '@auth/login-hint-store';
import { AuthenticationResult, EventType, BrowserAuthError } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ReactNode, useCallback, useEffect, useState, useRef } from 'react';
import { msalInstance } from './msal-instance';
import { loginSuccess } from '@state/auth-slice.ts';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchPermissionsQuery } from '@api/management/management-api.ts';
import {
  setOrganizationRolePermissions,
} from '@state/app-context-slice';
import { loginRequest, forgotPasswordRequest } from '@auth/auth-config.ts';
import { RootState } from '@state/root-state.ts';
import { logger } from '@utils/logging-utils';
import { useAuth, getIsLogoutInProgress } from '@hooks/useAuth';


interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Checks if the error is a browser-specific timeout or interaction required error
 */
const isSilentAuthError = (error: unknown): boolean => {
  if (error instanceof BrowserAuthError) {
    const errorMessage = error.errorMessage ?? '';
    const errorCodes = ['monitor_window_timeout', 'interaction_required', 'login_required', 'consent_required', 'interaction_in_progress'];

    const errorMessages = ['AADB2C90077', 'interaction_required', 'interaction_in_progress'];

    return errorCodes.includes(error.errorCode) || errorMessages.some(msg => errorMessage.includes(msg));
  }

  if (error instanceof Error) {
    const errorMessage = error.message ?? '';
    const hasErrorName = error.name === 'InteractionRequiredAuthError';
    const errorMessages = ['interaction_required', 'AADB2C90077', 'monitor_window_timeout', 'interaction_in_progress'];

    return hasErrorName || errorMessages.some(msg => errorMessage.includes(msg));
  }

  return false;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [token, setToken] = useState<string | null>(null);
  const [b2cObjectId, setB2cObjectId] = useState<string | null>(null);
  const { logout } = useAuth();

  // Use normal fetch instead of lazy - this will automatically cache and manage refetching
  const { data: permissions, error: permissionsError } = useFetchPermissionsQuery(
    { b2cObjectId: b2cObjectId ?? '', token: token ?? '' },
    {
      skip: !token || !b2cObjectId, // Skip if we don't have the required parameters
      refetchOnMountOrArgChange: 600 // Refetch if data is older than 10 minutes
    }
  );

  // Track if authentication initialization is in progress
  const authInitializationRef = useRef<boolean>(false);
  const forgotPasswordRedirectTriggeredRef = useRef<boolean>(false);
  const forgotPasswordRetryTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  const triggerForgotPasswordRedirect = useCallback((isRetry = false): void => {
    if (!isRetry && forgotPasswordRedirectTriggeredRef.current) {
      return;
    }

    if (!isRetry) {
      forgotPasswordRedirectTriggeredRef.current = true;
    }

    localStorage.removeItem('msal.interaction.status');

    msalInstance.loginRedirect(forgotPasswordRequest).catch(error => {
      if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress' && !isRetry) {
        forgotPasswordRetryTimeoutRef.current = globalThis.setTimeout(() => {
          triggerForgotPasswordRedirect(true);
        }, 200);
        return;
      }

      logger.error('Auth', 'Forgot password redirect failed', { error });
    });
  }, []);

  // Handle initial authentication state and MSAL events
  useEffect(() => {
    const forgotPasswordInUrl = globalThis.location.hash.includes('AADB2C90118') || globalThis.location.search.includes('AADB2C90118');
    if (forgotPasswordInUrl) {
      logger.info('Auth', 'Forgot password flow detected in URL, redirecting to password reset policy');
      triggerForgotPasswordRedirect();
      return;
    }

    // Prevent multiple initialization attempts
    if (authInitializationRef.current) {
      return;
    }

    // Don't initialize if logout is in progress
    if (getIsLogoutInProgress()) {
      logger.info('Auth', 'Logout in progress, skipping authentication initialization');
      return;
    }

    authInitializationRef.current = true;

    // Check for existing accounts
    const accounts = msalInstance.getAllAccounts();

    if (accounts && accounts.length > 0) {
      const loginHintStore = new LoginHintStore();
      loginHintStore.setLoginHint(accounts[0].localAccountId);
      msalInstance.setActiveAccount(accounts[0]);
      msalInstance.enableAccountStorageEvents();
      dispatch(loginSuccess(accounts[0].localAccountId));
      setB2cObjectId(accounts[0].localAccountId);

      logger.info('Auth', 'Existing account found and set as active', {
        accountId: accounts[0].localAccountId,
        username: accounts[0].username
      });
    } else {
      const storedLoginHint = new LoginHintStore().getLoginHint();
      if (storedLoginHint) {
        logger.info('Auth', 'Attempting silent SSO with stored login hint');

        msalInstance.ssoSilent({ loginHint: storedLoginHint }).catch(error => {
          if (isSilentAuthError(error)) {
            // Special handling for interaction_in_progress
            if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
              logger.info('Auth', 'Authentication interaction already in progress during SSO silent, waiting for completion', {
                errorCode: error.errorCode,
                timestamp: new Date().toISOString()
              });
              return;
            }

            logger.warn('Auth', 'Silent sign-in failed, user interaction will be required', {
              error: error instanceof Error ? error.message : String(error),
              errorCode: error instanceof BrowserAuthError ? error.errorCode : 'unknown'
            });
          } else {
            logger.error('Auth', 'Silent sign-in error', { error });
          }
        });
      }
    }

    // Set up MSAL event listener
    const callbackId = msalInstance.addEventCallback(event => {
      const authenticationResult = event.payload as AuthenticationResult;
      const account = authenticationResult?.account;

      if (event.eventType === EventType.LOGIN_SUCCESS && account) {
        // Don't process login success if logout is in progress
        if (getIsLogoutInProgress()) {
          logger.info('Auth', 'Logout in progress, ignoring login success event');
          return;
        }

        logger.info('Auth', 'Login success event received', {
          accountId: account.localAccountId,
          username: account.username
        });

        msalInstance.setActiveAccount(account);
        dispatch(loginSuccess(account.localAccountId));
        setB2cObjectId(account.localAccountId);
      } else if (event.eventType === EventType.LOGIN_FAILURE) {
        const errorDescription =
          event.error instanceof Error
            ? event.error.message
            : String(event.error ?? '');

        if (errorDescription.includes('AADB2C90118')) {
          logger.info('Auth', 'Forgot password login failure detected, redirecting to password reset policy');
          triggerForgotPasswordRedirect();
        }
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        logger.info('Auth', 'Logout success event received');

        // Clear local state
        setToken(null);
        setB2cObjectId(null);

        // Reset initialization flag to allow re-initialization
        authInitializationRef.current = false;
      }
    });

    // Cleanup callback on unmount
    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
      if (forgotPasswordRetryTimeoutRef.current !== null) {
        globalThis.clearTimeout(forgotPasswordRetryTimeoutRef.current);
        forgotPasswordRetryTimeoutRef.current = null;
      }
      authInitializationRef.current = false;
    };
  }, [dispatch, triggerForgotPasswordRedirect]);

  // Handle token acquisition
  useEffect(() => {
    const tokenFetch = async () => {
      try {
        // Don't fetch tokens if logout is in progress
        if (getIsLogoutInProgress()) {
          logger.info('Auth', 'Logout in progress, skipping token acquisition');
          return;
        }

        const result = await msalInstance.acquireTokenSilent(loginRequest);
        setToken(result.accessToken);

        logger.debug('Auth', 'Token acquired successfully');
      } catch (error) {
        // Check if this is a silent auth error that requires user interaction
        if (isSilentAuthError(error)) {
          // Special handling for interaction_in_progress
          if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
            logger.info('Auth', 'Authentication interaction already in progress during token acquisition, waiting for completion', {
              errorCode: error.errorCode,
              timestamp: new Date().toISOString()
            });
            // Don't trigger logout for interaction_in_progress, just wait
            return;
          }

          logger.warn('Auth', 'Session expired or interaction required, redirecting to login', {
            errorName: error instanceof Error ? error.name : 'unknown',
            errorCode: error instanceof BrowserAuthError ? error.errorCode : 'unknown',
            timestamp: new Date().toISOString()
          });

          // Use the hook's logout functionality to clear state and redirect
          logout().catch(logoutError => {
            logger.error('Auth', 'MSAL logout error', { error: logoutError });
          });
        } else {
          // Log other types of errors but don't automatically logout
          logger.error('Auth', 'Token acquisition failed', { error });
        }
      }
    };

    if (b2cObjectId && !token && !getIsLogoutInProgress()) {
      tokenFetch();
    }
  }, [isAuthenticated, b2cObjectId, token, logout]);

  // Handle permissions data and error logging
  useEffect(() => {
    // Log permission loading errors
    if (permissionsError) {
      logger.error('AuthProvider', 'Error loading permissions', {
        error: permissionsError,
        b2cObjectId
      });
      return;
    }

    if (!permissions) return;

    dispatch(setOrganizationRolePermissions(permissions.organizationRolePermissions));

  }, [permissions, permissionsError, dispatch, b2cObjectId]);

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};
